import { VestfrostClaims } from './services/auth.service';

/**
 * Which organization a list request should ask for.
 *
 * The API will not infer the caller's scope: a response is a function of its explicit parameters,
 * never of who is asking, so `GET /Device` with no `organizationId` is 403 for everyone except a
 * system administrator. The client therefore sends its own organization id, and the backend expands
 * that to the caller's **visible subtree** — own organization plus every descendant whose
 * `upstreamManagementAllowed` chain is open. There is nothing extra to fetch and no tree to walk
 * here; the id from the token is the whole question.
 *
 * A system administrator sends **nothing**, because for them the unscoped list is the cross-tenant
 * list and naming one organization would narrow it.
 */

/**
 * The `organizationId` to send, or `null` to omit the parameter entirely.
 *
 * `null` means *omit*, and that is not the same as `0` — `?organizationId=0` is a request for an
 * organization that does not exist, where omitting asks for the caller's default scope. See the
 * "never default an optional id to 0" rule in CLAUDE.md.
 *
 * A non-admin whose token carries no `organization_id` also yields `null`, so the request goes out
 * unscoped and comes back 403 exactly as any unscoped call does. That is deliberate: the claim is
 * always present in practice, and inventing a client-side failure for it would mean wrapping every
 * list call in `defer()` to keep a synchronous throw from escaping past its `error:` handler.
 */
export function scopedOrganizationId(
  claims: Pick<VestfrostClaims, 'isSystemAdmin' | 'organizationId'>,
): number | null {
  return claims.isSystemAdmin ? null : claims.organizationId;
}
